/**
 * Die schlanke Kopfzeile des Neuentwurfs (REQ-20 §4): links der Breadcrumb,
 * rechts Hell/Dunkel-Umschalter und Benutzer-Chip.
 *
 * **Ohne Suchfeld und ohne Glocke** — der Entwurf zeigt beide (Zeilen 158–166),
 * der Auftraggeber hat sie ersatzlos gestrichen (REQ-20 §2.3).
 */

import type { ReactElement } from 'react';
import { useLocation } from 'react-router-dom';

import type { ChinaAppSysGroupCategory } from '@/types/entities';
import { buildViewHref } from '@/utils/viewNavigation';

import { ProfileTooltip } from './ProfileTooltip';
import { ThemeToggle } from './ThemeToggle';

export interface AppHeaderProps {
  categories: ChinaAppSysGroupCategory[];
}

interface Breadcrumb {
  /** Kategorie der offenen Ansicht; auf dem Dashboard `null`. */
  section: string | null;
  page: string;
}

/**
 * Breadcrumb aus der aktuellen Route. Gesucht wird die Ansicht, deren Ziel dem
 * Pfad entspricht — dieselbe Zuordnung, die auch die Navigation benutzt
 * (`buildViewHref`). Auf dem Dashboard steht nur „Dashboard"; für Routen ohne
 * Ansicht (Seite nicht gefunden) bleibt der Breadcrumb leer.
 */
function breadcrumbFor(pathname: string, categories: ChinaAppSysGroupCategory[]): Breadcrumb {
  if (pathname === '/') {
    return { section: null, page: 'Dashboard' };
  }

  for (const category of categories) {
    const view = (category.Views ?? []).find((entry) => buildViewHref(entry) === pathname);
    if (view !== undefined) {
      return { section: category.Bezeichnung, page: view.ViewName ?? '' };
    }
  }

  return { section: null, page: '' };
}

export function AppHeader({ categories }: AppHeaderProps): ReactElement {
  const { pathname } = useLocation();
  const { section, page } = breadcrumbFor(pathname, categories);

  return (
    <header className="iwm-app-header">
      <nav className="iwm-breadcrumb" aria-label="Navigationspfad">
        {section === null ? null : (
          <>
            <span className="iwm-breadcrumb-section">{section}</span>
            <span className="iwm-breadcrumb-separator" aria-hidden="true">
              /
            </span>
          </>
        )}
        <span className="iwm-breadcrumb-page" aria-current="page">
          {page}
        </span>
      </nav>

      <div className="iwm-app-header-spacer" />

      <ThemeToggle />
      <ProfileTooltip />
    </header>
  );
}

export default AppHeader;
